import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Users, UserCheck, RefreshCw, Headphones,
  Landmark, Rocket, Shield, TrendingUp, Heart, CheckCircle2,
} from 'lucide-react';
import { ROUTES } from '@/router/routes';

// ─── DATA ────────────────────────────────────────────────────────────────────

const WHY_CARDS = [
  {
    num: '01',
    Icon: Users,
    title: 'Purpose-Built for People',
    body: 'Role-based interfaces deliver what every employee needs. Nothing more, nothing less.',
    checks: [],
  },
  {
    num: '02',
    Icon: UserCheck,
    title: 'Implementation Until Adoption',
    body: 'We stay with your team beyond go-live—guiding, training and supporting until using SkolarMinds becomes second nature.',
    checks: [],
  },
  {
    num: '03',
    Icon: RefreshCw,
    title: 'One Action. Everything Syncs.',
    body: 'Enter once and it updates everywhere. Admissions, academics, attendance, finance and communication—always in sync.',
    checks: [],
  },
  {
    num: '04',
    Icon: Headphones,
    title: 'Support You Can Rely On',
    body: 'Real people. Real support. Always available when you need us.',
    checks: ['24×7 Ticket Portal', 'Resolution in 24–48 hrs', 'Priority for critical issues', 'Proactive follow-ups'],
  },
  {
    num: '05',
    Icon: Landmark,
    title: 'Built for Indian Institutions',
    body: 'Designed around real workflows, compliance needs, multi-campus realities and diverse user capabilities.',
    checks: [],
  },
  {
    num: '06',
    Icon: Rocket,
    title: 'Partnership Beyond Go-Live',
    body: 'Regular reviews, process improvement, new user onboarding and feature guidance—because your success is our success.',
    checks: [],
  },
];

const BOTTOM_CARDS = [
  {
    Icon: Users,
    title: 'Role-Based Simplicity',
    body: 'Every employee gets a personalized workspace.',
  },
  {
    Icon: RefreshCw,
    title: 'One-Time Data Entry',
    body: 'Updates sync instantly across the platform.',
  },
  {
    Icon: Heart,
    title: 'Implementation Until Adoption',
    body: 'We guide your team until it becomes habit.',
  },
  {
    Icon: Shield,
    title: '24×7 Support Portal',
    body: 'Most tickets resolved within 24–48 hours.',
  },
  {
    Icon: TrendingUp,
    title: 'Continuous Success Partnership',
    body: 'Regular guidance to help your institution grow.',
  },
];

// ─── ANIMATION HELPERS ────────────────────────────────────────────────────────

const MotionBox = motion(Box);

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.52, delay, ease: [0.22, 1, 0.36, 1] as number[] },
});

// ─── COMPONENT ────────────────────────────────────────────────────────────────

export default function WhySkolarMindsPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #F8F7FF 0%, #F3F0FF 50%, #EEF2FF 100%)',
      fontFamily: '"DM Sans", sans-serif',
      position: 'relative',
      overflow: 'hidden',
    }}>

      {/* ── Decorative blobs ── */}
      <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
        <Box sx={{ position: 'absolute', top: '-10%', right: '-8%', width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(99,102,241,0.07), transparent 70%)', borderRadius: '50%' }} />
        <Box sx={{ position: 'absolute', bottom: '-8%', left: '-6%', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(124,99,216,0.06), transparent 70%)', borderRadius: '50%' }} />
        <Box sx={{ position: 'absolute', top: '40%', left: '30%', width: '700px', height: '300px', background: 'radial-gradient(ellipse, rgba(99,102,241,0.04), transparent 70%)' }} />
      </Box>

      {/* ── Fixed navbar ── */}
      <Box sx={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        bgcolor: 'rgba(248,247,255,0.90)', backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(99,102,241,0.10)',
        px: { xs: 2, sm: 4, md: 6 }, height: '64px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Button
          onClick={() => navigate(ROUTES.PUBLIC.HOME)}
          startIcon={<ArrowLeft size={15} />}
          sx={{
            color: '#6366F1', fontSize: '13px', fontWeight: 500,
            fontFamily: '"DM Sans", sans-serif', minWidth: 0,
            '&:hover': { color: '#4F46E5', bgcolor: 'transparent' },
          }}
        >
          Home
        </Button>
        <Typography sx={{
          fontFamily: '"Syne", sans-serif', fontWeight: 800,
          fontSize: '15px', color: '#111827', letterSpacing: '-0.01em',
        }}>
          Skolar<Box component="span" sx={{ color: '#6366F1' }}>Minds</Box>
        </Typography>
        <Button
          onClick={() => navigate(ROUTES.PUBLIC.HOME, { state: { openDemo: true } })}
          variant="contained"
          size="small"
          sx={{
            bgcolor: '#6366F1', color: '#fff', borderRadius: '8px',
            fontSize: '12px', fontWeight: 600, px: '16px', py: '7px',
            fontFamily: '"DM Sans", sans-serif', textTransform: 'none',
            boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
            '&:hover': { bgcolor: '#4F46E5' },
          }}
        >
          Book a Demo
        </Button>
      </Box>

      {/* ── Main content ── */}
      <Box sx={{ position: 'relative', zIndex: 1, pt: '96px', pb: '80px' }}>
        <Container maxWidth="lg">

          {/* ── Hero text ── */}
          <MotionBox {...fadeUp(0)} sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography sx={{
              fontSize: '11px', fontWeight: 700, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: '#6366F1', mb: 2,
            }}>
              Why SkolarMinds
            </Typography>
            <Typography variant="h1" sx={{
              fontFamily: '"Syne", sans-serif',
              fontSize: { xs: '28px', sm: '38px', md: '52px' },
              fontWeight: 800, color: '#0F0A1E', lineHeight: 1.1,
              mb: 3, letterSpacing: '-0.02em',
            }}>
              Built for Adoption.{' '}
              <Box component="span" sx={{ color: '#6366F1' }}>Designed for Impact.</Box>
            </Typography>
            <Typography sx={{
              fontSize: { xs: '15px', md: '17px' }, color: '#475569',
              maxWidth: '600px', mx: 'auto', lineHeight: 1.75, fontWeight: 400,
            }}>
              SkolarMinds is more than an ERP. It's a connected ecosystem built around people,
              with unwavering support until your institution runs better every single day.
            </Typography>
          </MotionBox>

          {/* ── 6-card feature grid ── */}
          <MotionBox {...fadeUp(0.1)} sx={{ mb: { xs: 4, md: 5 } }}>
            <Grid container spacing={{ xs: 2, md: 2 }}>
              {WHY_CARDS.map((card, i) => {
                const { Icon } = card;
                return (
                  <Grid item xs={12} sm={4} md={2} key={card.num}>
                    <Box sx={{
                      p: { xs: '24px', md: '26px 20px' },
                      height: '100%',
                      bgcolor: '#fff',
                      borderRadius: '14px',
                      border: '1.5px solid rgba(99,102,241,0.18)',
                      boxShadow: '0 2px 16px rgba(99,102,241,0.05)',
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        opacity: 0,
                        transition: 'opacity 0.35s ease',
                        background: 'radial-gradient(ellipse at center top, rgba(99,102,241,0.12), transparent 68%)',
                        pointerEvents: 'none',
                      },
                      '&:hover': {
                        borderColor: '#6366F1',
                        boxShadow: '0 0 0 1px #6366F1, 0 8px 32px rgba(99,102,241,0.18)',
                        transform: 'translateY(-2px)',
                      },
                      '&:hover::before': { opacity: 1 },
                    }}>
                      {/* Number + underline */}
                      <Typography sx={{
                        fontSize: '13px', fontWeight: 700, color: '#6366F1',
                        mb: '8px', fontFamily: '"Syne", sans-serif',
                      }}>
                        {card.num}
                      </Typography>
                      <Box sx={{ width: '24px', height: '2px', bgcolor: '#6366F1', borderRadius: '2px', mb: '18px', opacity: 0.7 }} />

                      {/* Title */}
                      <Typography sx={{
                        fontSize: { xs: '15px', md: '16px' },
                        fontWeight: 700, color: '#0F172A',
                        lineHeight: 1.35, mb: '10px',
                        fontFamily: '"Syne", sans-serif',
                      }}>
                        {card.title}
                      </Typography>

                      {/* Body */}
                      <Typography sx={{
                        fontSize: '13.5px', color: '#64748B',
                        lineHeight: 1.7, mb: card.checks.length ? '14px' : 0,
                      }}>
                        {card.body}
                      </Typography>

                      {/* Checklist (card 04 only) */}
                      {card.checks.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {card.checks.map(c => (
                            <Box key={c} sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                              <CheckCircle2 size={13} color="#6366F1" />
                              <Typography sx={{ fontSize: '12.5px', color: '#475569', fontWeight: 500 }}>
                                {c}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </MotionBox>

          {/* ── 5-icon bottom row ── */}
          <MotionBox {...fadeUp(0.2)}>
            <Box sx={{
              bgcolor: '#fff',
              borderRadius: '20px',
              border: '1px solid rgba(99,102,241,0.10)',
              boxShadow: '0 4px 40px rgba(99,102,241,0.06)',
              overflow: 'hidden',
            }}>
              <Grid container>
                {BOTTOM_CARDS.map((card, i) => {
                  const { Icon } = card;
                  return (
                    <Grid item xs={12} sm={6} md key={card.title}>
                      <Box sx={{
                        p: { xs: '24px', md: '28px 24px' },
                        textAlign: 'center',
                        borderRight: { md: i < BOTTOM_CARDS.length - 1 ? '1px solid rgba(99,102,241,0.08)' : 'none' },
                        borderBottom: { xs: i < BOTTOM_CARDS.length - 1 ? '1px solid rgba(99,102,241,0.08)' : 'none', md: 'none' },
                        transition: 'background 0.2s',
                        '&:hover': { bgcolor: 'rgba(99,102,241,0.02)' },
                        '&:hover .why-icon-box': {
                          bgcolor: '#C7D2FE',
                          transform: 'scale(1.12) rotate(-5deg)',
                        },
                      }}>
                        {/* Icon */}
                        <Box className="why-icon-box" sx={{
                          width: 52, height: 52, borderRadius: '14px',
                          bgcolor: '#EEF2FF', mx: 'auto', mb: '14px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all 0.25s ease',
                        }}>
                          <Icon size={22} color="#6366F1" strokeWidth={1.6} />
                        </Box>
                        <Typography sx={{
                          fontSize: '13.5px', fontWeight: 700, color: '#0F172A',
                          mb: '6px', fontFamily: '"Syne", sans-serif', lineHeight: 1.3,
                        }}>
                          {card.title}
                        </Typography>
                        <Typography sx={{ fontSize: '12.5px', color: '#64748B', lineHeight: 1.65 }}>
                          {card.body}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </MotionBox>

          {/* ── CTA ── */}
          <MotionBox {...fadeUp(0.3)} sx={{ textAlign: 'center', mt: { xs: 6, md: 8 } }}>
            <Typography sx={{
              fontSize: { xs: '20px', md: '26px' }, fontWeight: 700,
              color: '#0F172A', mb: 1.5, fontFamily: '"Syne", sans-serif',
            }}>
              Ready to experience the difference?
            </Typography>
            <Typography sx={{ fontSize: '15px', color: '#64748B', mb: 4, maxWidth: '420px', mx: 'auto' }}>
              Join institutions already transforming with SkolarMinds.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={() => navigate(ROUTES.PUBLIC.HOME, { state: { openDemo: true } })}
                sx={{
                  bgcolor: '#6366F1', color: '#fff', borderRadius: '10px',
                  px: '28px', py: '12px', fontSize: '14px', fontWeight: 600,
                  fontFamily: '"DM Sans", sans-serif', textTransform: 'none',
                  boxShadow: '0 8px 24px rgba(99,102,241,0.35)',
                  '&:hover': { bgcolor: '#4F46E5', boxShadow: '0 12px 32px rgba(99,102,241,0.45)' },
                }}
              >
                Book a Demo
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(ROUTES.PUBLIC.TRANSFORMATION)}
                sx={{
                  borderColor: 'rgba(99,102,241,0.35)', color: '#6366F1',
                  borderRadius: '10px', px: '28px', py: '12px',
                  fontSize: '14px', fontWeight: 600,
                  fontFamily: '"DM Sans", sans-serif', textTransform: 'none',
                  '&:hover': { borderColor: '#6366F1', bgcolor: 'rgba(99,102,241,0.04)' },
                }}
              >
                See The Shift
              </Button>
            </Box>
          </MotionBox>

        </Container>
      </Box>
    </Box>
  );
}
